import React from 'react';
import PropTypes from 'prop-types';
import {
  Content, TextEditor,
} from '../../../components';
import BenefitTable, {
  BenefitTh,
  BenefitTr,
  BenefitTd,
  Legend,
} from './BenefitTable';
import { CONTENT_TYPE_FAST } from '../../../components/Content/Content';

const propTypes = {
  plans: PropTypes.arrayOf(PropTypes.shape({
    carrierId: PropTypes.number,
    cmsContractId: PropTypes.string,
    cmsPbpId: PropTypes.string,
    id: PropTypes.number,
    name: PropTypes.string,
    planBenefits: PropTypes.arrayOf(PropTypes.shape({
      category: PropTypes.string,
      id: PropTypes.number,
      name: PropTypes.string,
      order: PropTypes.number,
      planId: PropTypes.number,
      subcategory: PropTypes.string,
      value: PropTypes.string,
    })),
    region: PropTypes.shape({
      carrierId: PropTypes.number,
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    regionId: PropTypes.number,
  })),
  editAllowed: PropTypes.bool,
  editBenefitId: PropTypes.number,
  editSubCategoryName: PropTypes.shape({
    value: PropTypes.string,
    benefitName: PropTypes.string,
    categoryName: PropTypes.string,
  }),
  editBenefitName: PropTypes.shape({
    value: PropTypes.string,
    categoryName: PropTypes.string,
    benefitName: PropTypes.string,
  }),
  editCategoryName: PropTypes.shape({

  }),
  onStartEdit: PropTypes.func.isRequired,
  onSaveEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  frameless: PropTypes.bool,
};
const defaultProps = {
  plans: null,
  editAllowed: false,
  editBenefitId: null,
  editSubCategoryName: null,
  editBenefitName: null,
  editCategoryName: null,
  frameless: false,
};

export default function CompareBenefits(props) {
  const {
    plans,
    editAllowed,
    editBenefitId,
    editBenefitName,
    editCategoryName,
    editSubCategoryName,
    onStartEdit,
    frameless,
    onSaveEdit,
    onCancelEdit,
  } = props;
  const planBenefits = [];
  const planIds = [];
  plans.forEach((plan) => {
    planIds.push(plan.id);
    plan.planBenefits.forEach((benefit) => {
      if (benefit.planBenefitCategory) {
        // when comparing benefits, always get the name from planBenefitCategory if it exists.
        const changedBenefitName = { ...benefit, name: benefit.planBenefitCategory.name };
        planBenefits.push(changedBenefitName);
      } else planBenefits.push(benefit);
    });
  });
  planBenefits
    .sort((a, b) => {
      if (a.order === b.order) {
        return a.id - b.id;
      }
      return a.order - b.order;
    });

  const uniqueCategories = [...new Set(planBenefits.map(b => b.category))];
  const result = uniqueCategories.map((category, categoryIndex) => {
    const benefitsInCategory = planBenefits.filter(b => b.category === category);
    const subcategories = [...new Set(benefitsInCategory.map(b => b.subcategory))];
    const benefitNames = [...new Set(benefitsInCategory.map(b => b.name))];
    const benefitRows = benefitNames.map((benefitName) => {
      const benefitsWithName = benefitsInCategory.filter(b => b.name === benefitName);
      const model = subcategories.map((subcategory, index) => {
        const filterNames = benefitsWithName
          .filter(benefit => benefit.subcategory === subcategory);
        const benefitValues = [];
        let flag = false;
        for (let i = 0; i < planIds.length; i += 1) {
          for (let j = 0; j < filterNames.length; j += 1) {
            if (planIds[i] === filterNames[j].planId) {
              const editMode = editAllowed && editBenefitId === filterNames[j].id;
              benefitValues.push(
                <BenefitTd
                  name="editBenefitId"
                  key={filterNames[j].id}
                  editAvailable={editAllowed && !editMode}
                  value={filterNames[j].id}
                  onClick={editMode === true ? null : onStartEdit}
                >
                  <Content
                    value={filterNames[j].value}
                    edit={editMode}
                    inherit
                    onSave={value => onSaveEdit(value, filterNames[j].planId)}
                    onCancel={onCancelEdit}
                    type={CONTENT_TYPE_FAST.PLAIN}
                  />
                </BenefitTd>,
              );
              flag = true;
              break;
            }
          }
          if (!flag) {
            benefitValues.push(<BenefitTd key={`na-${planIds[i]}`}>N/A</BenefitTd>);
          }
          flag = false;
        }
        const isEditable = editAllowed
                          && editBenefitName
                          && editBenefitName.value === benefitName
                          && editBenefitName.categoryName === category;

        const renderBenefitName = (
          <BenefitTh columnWidth="21%" rowSpan={subcategories.length} name="editBenefitName" value={benefitName} onClick={isEditable === true ? null : event => onStartEdit(event, undefined, category)} editAvailable={editAllowed}>
            <Content
              edit={isEditable}
              inherit
              onSave={onSaveEdit}
              onCancel={onCancelEdit}
              type={CONTENT_TYPE_FAST.PLAIN}
              value={benefitName}
            />
          </BenefitTh>
        );
        const subCategoryEditMode = editSubCategoryName
                                    && editAllowed
                                    && editSubCategoryName.value === subcategory
                                    && editSubCategoryName.benefitName === benefitName
                                    && editSubCategoryName.categoryName === category;
        const renderSubCategory = (
          <Content
            edit={subCategoryEditMode}
            inherit
            onSave={value => onSaveEdit(value, undefined, benefitName)}
            onCancel={onCancelEdit}
            type={CONTENT_TYPE_FAST.PLAIN}
            value={subcategory}
          />
        );
        return (
          <BenefitTr key={subcategory}>
            {index === 0
              ? renderBenefitName
              : null}
            <BenefitTh
              columnWidth="9%"
              name="editSubCategoryName"
              value={subcategory}
              onClick={
                subCategoryEditMode === true
                  ? null
                  : event => onStartEdit(event, benefitName, category)
              }
              editAvailable={editAllowed && !subCategoryEditMode}
              benefitName={benefitName}
            >
              {renderSubCategory}
            </BenefitTh>
            {benefitValues}
          </BenefitTr>
        );
      });
      return model;
    });
    const planHeaders = plans.map(plan => (
      <BenefitTh key={plan.id}>
        {plan.name}
      </BenefitTh>
    ));
    const categoryEditMode = !!editAllowed && editCategoryName === category;
    let categoryContent;
    if (categoryEditMode) {
      categoryContent = (
        <TextEditor
          defaultValue={category}
          onCancel={onCancelEdit}
          onSave={onSaveEdit}
          autoSize
          rows={1}
        />
      );
    } else {
      categoryContent = category;
    }
    return (
      <div key={category}>
        <Legend
          name="editCategoryName"
          value={category}
          editAvailable={editAllowed && !categoryEditMode}
          onClick={categoryEditMode === true ? null : onStartEdit}
        >
          {categoryContent}
        </Legend>
        <BenefitTable>
          {(categoryIndex === 0 && !frameless) ? (
            <thead>
              <BenefitTr>
                <BenefitTh colSpan="2" columnWidth="30%" />
                {categoryIndex === 0 ? planHeaders : null}
              </BenefitTr>
            </thead>
          ) : null }
          <tbody>
            {benefitRows}
          </tbody>
        </BenefitTable>
      </div>
    );
  });
  return result;
}

CompareBenefits.defaultProps = defaultProps;
CompareBenefits.propTypes = propTypes;
